import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/** A picture in the company's image library, placed on designs as `image` elements. */
export interface DesignImage {
  public_id: string;
  url: string; // path under the API base; needs the bearer token
  width_px: number;
  height_px: number;
  bytes: number;
  created_at: string;
}

export const IMAGE_LIBRARY_MAX_BYTES = 15 * 1024 * 1024; // a phone photo as JPEG is often 4 to 8 MB

@Injectable({ providedIn: 'root' })
export class CompanyImageService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/voucher-designs/images`;

  list(): Observable<{ images: DesignImage[] }> {
    return this.http.get<{ images: DesignImage[] }>(this.baseUrl);
  }

  /** PNG, JPEG or WebP up to 5 MB; re-encoded and bounded to 2000 px on the server. */
  upload(file: File): Observable<DesignImage> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<DesignImage>(this.baseUrl, formData);
  }

  /** 409 while a saved design still places the picture. */
  delete(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/${id}`);
  }

  /** The absolute URL of a picture, for `app-authenticated-image`. */
  fileUrl(image: DesignImage): string {
    return `${environment.apiUrl}${image.url.startsWith('/') ? image.url : `/${image.url}`}`;
  }

  /** The picture's bytes (through the auth interceptor), for the editor canvas. */
  fileBlob(image: DesignImage): Observable<Blob> {
    return this.http.get(this.fileUrl(image), { responseType: 'blob' });
  }
}
